// src/components/ReuploadFilesDialog/index.tsx
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Download, Upload, CircleCheck, AlertCircle } from 'lucide-react';
import apiReq, { backendHost } from 'core/api';
import { tokenStorage } from 'utils';
import { uploadFileChunked, PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING } from 'utils/chunkedFileUpload';
import { processContextDocumentParsing } from 'modules/estimation/api';
import { contextDocumentEvents } from 'modules/chat/utils/contextDocumentEvents';

interface ContextFileListing {
  id: string;
  name: string;
}

export interface ReuploadReplacement {
  /** id старого документа (null, если не нашли по имени в листинге) */
  oldId: string | null;
  /** id нового документа после перезагрузки */
  newId: string;
}

interface ReuploadFilesDialogProps {
  open: boolean;
  /** Имена проблемных файлов из текста 422 */
  fileNames: string[];
  projectId: string;
  /** Куда заливать новый документ */
  targetType: 'project' | 'presale';
  targetId: string;
  onClose: () => void;
  /** Вызывается при закрытии, если была хотя бы одна успешная перезагрузка */
  onReuploaded?: (replacements: ReuploadReplacement[]) => void;
}

type RowStatus = 'idle' | 'uploading' | 'parsing' | 'done' | 'error';

interface RowState {
  status: RowStatus;
  progress: number;
  error?: string;
  newDocumentId?: string;
}

const ReuploadFilesDialog = ({
  open,
  fileNames,
  projectId,
  targetType,
  targetId,
  onClose,
  onReuploaded,
}: ReuploadFilesDialogProps) => {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const processToNameRef = useRef<Map<string, string>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingNameRef = useRef<string | null>(null);

  // Листинг проекта — матчим имена из 422 на id документов (для download и замены выбора)
  const { data: listing = [] } = useQuery({
    queryKey: ['contextDocuments', projectId],
    queryFn: async () => {
      const result = await apiReq<ContextFileListing[]>(
        `projects/context-documents?project_id=${projectId}`,
        'GET'
      );
      return result || [];
    },
    enabled: open && !!projectId,
  });

  // Сброс строк при открытии диалога
  useEffect(() => {
    if (open) {
      setRows({});
      // Чистим карту process_id → name: запоздавшее WS-событие прошлой
      // сессии диалога не должно перезаписать свежий row.
      processToNameRef.current.clear();
    }
  }, [open]);

  // Завершение парсинга нового документа приходит по WS
  useEffect(() => {
    if (!open) return;
    const off = contextDocumentEvents.on((payload) => {
      const name = processToNameRef.current.get(payload.process_id);
      if (!name) return;
      if (payload.status === 'success') {
        setRows((prev) => ({
          ...prev,
          [name]: { status: 'done', progress: 100, newDocumentId: payload.document_id },
        }));
        queryClient.invalidateQueries({ queryKey: ['contextDocuments', projectId] });
      } else if (payload.status === 'error') {
        setRows((prev) => ({
          ...prev,
          [name]: { status: 'error', progress: 0, error: payload.message || 'Ошибка парсинга' },
        }));
      }
    });
    return off;
  }, [open, projectId, queryClient]);

  const findOldId = (name: string): string | null =>
    listing.find((f) => f.name === name)?.id ?? null;

  const handleDownload = async (name: string) => {
    const oldId = findOldId(name);
    if (!oldId) return;
    try {
      const token = tokenStorage.get();
      const res = await fetch(`${backendHost}/projects/context-documents/${oldId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        setRows((prev) => ({
          ...prev,
          [name]: { status: 'error', progress: 0, error: 'Не удалось скачать файл' },
        }));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setRows((prev) => ({
        ...prev,
        [name]: { status: 'error', progress: 0, error: 'Не удалось скачать файл' },
      }));
    }
  };

  const handlePickFile = (name: string) => {
    pendingNameRef.current = name;
    fileInputRef.current?.click();
  };

  const handleFileChosen = async (file: File | undefined) => {
    const name = pendingNameRef.current;
    pendingNameRef.current = null;
    if (!file || !name) return;

    setRows((prev) => ({ ...prev, [name]: { status: 'uploading', progress: 0 } }));
    try {
      const fileId = await uploadFileChunked(
        file,
        projectId,
        PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING,
        (percent) => setRows((prev) => ({ ...prev, [name]: { ...prev[name], progress: percent } }))
      );
      const { process_id } = await processContextDocumentParsing(fileId, targetType, targetId);
      processToNameRef.current.set(process_id, name);
      setRows((prev) => ({ ...prev, [name]: { status: 'parsing', progress: 100 } }));
    } catch (e) {
      setRows((prev) => ({
        ...prev,
        [name]: {
          status: 'error',
          progress: 0,
          error: e instanceof Error ? e.message : 'Ошибка загрузки',
        },
      }));
    }
  };

  const handleClose = () => {
    const replacements: ReuploadReplacement[] = Object.entries(rows)
      .filter(([, r]) => r.status === 'done' && r.newDocumentId)
      .map(([name, r]) => ({ oldId: findOldId(name), newId: r.newDocumentId as string }));
    if (replacements.length > 0) onReuploaded?.(replacements);
    onClose();
  };

  const hasPending = Object.values(rows).some(
    (r) => r.status === 'uploading' || r.status === 'parsing'
  );

  return (
    <Dialog open={open} onClose={hasPending ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Файлы нужно загрузить повторно</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          После обновления системы эти документы недоступны для ИИ. Скачайте наш экземпляр и
          загрузите его заново — либо выберите файл со своего диска.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {fileNames.map((name) => {
            const row = rows[name] ?? { status: 'idle' as RowStatus, progress: 0 };
            return (
              <Box
                key={name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {name}
                  </Typography>
                  {row.status === 'uploading' && (
                    <LinearProgress variant="determinate" value={row.progress} sx={{ mt: 0.5 }} />
                  )}
                  {row.status === 'parsing' && <LinearProgress sx={{ mt: 0.5 }} />}
                  {row.status === 'error' && (
                    <Typography variant="caption" color="error">
                      {row.error}
                    </Typography>
                  )}
                </Box>
                {row.status === 'done' ? (
                  <CircleCheck size={18} color="var(--mui-palette-success-main, #2e7d32)" />
                ) : row.status === 'error' ? (
                  <AlertCircle size={18} color="var(--mui-palette-error-main, #d32f2f)" />
                ) : null}
                {(row.status === 'idle' || row.status === 'error') && (
                  <>
                    {findOldId(name) && (
                      <Tooltip title="Скачать наш экземпляр">
                        <IconButton size="small" onClick={() => void handleDownload(name)}>
                          <Download size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Загрузить заново">
                      <IconButton size="small" onClick={() => handlePickFile(name)}>
                        <Upload size={16} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => {
            void handleFileChosen(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={hasPending}>
          {Object.values(rows).some((r) => r.status === 'done') ? 'Готово' : 'Закрыть'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReuploadFilesDialog;
